"use client"

import { useState, useContext } from "react"
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
  alpha,
  Fade,
} from "@mui/material"
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Login as LoginIcon,
  CreditCard as CreditCardIcon,
} from "@mui/icons-material"
import axios from "axios"
import { AppContext } from "../contexts/AppContext"
import { useLocation, useNavigate } from "react-router-dom"
import { styled } from "@mui/material/styles"

// Styled components
const LoginContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  position: "relative",
  overflow: "hidden",
  padding: 0,
  maxWidth: "100%",
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%)`,
}))

const GlassCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  backdropFilter: "blur(10px)",
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  maxWidth: "450px",
  width: "100%",
  position: "relative",
  zIndex: 10,
  overflow: "visible",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -20,
    left: -20,
    right: -20,
    bottom: -20,
    background: "linear-gradient(45deg, transparent 50%, rgba(255,255,255,0.1) 100%)",
    zIndex: -1,
    borderRadius: theme.spacing(3),
    filter: "blur(20px)",
  },
}))

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1.5),
    transition: "all 0.3s ease",
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
    "&:hover": {
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
    },
    "&.Mui-focused": {
      backgroundColor: alpha(theme.palette.background.paper, 1),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
  },
}))

const LoginButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  fontWeight: "bold",
  textTransform: "none",
  fontSize: "1rem",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  transition: "all 0.3s ease",
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  "&:hover": {
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
    transform: "translateY(-2px)",
  },
}))

// Decorative elements
const Circle = styled(Box)(({ theme, size, top, left, right, bottom, opacity }) => ({
  position: "absolute",
  width: size,
  height: size,
  borderRadius: "50%",
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, opacity)} 0%, ${alpha(theme.palette.secondary.light, opacity)} 100%)`,
  top,
  left,
  right,
  bottom,
  zIndex: 1,
  filter: "blur(40px)",
}))

const Login = () => {
  const [email, setEmail] = useState(""); //useState("youn.henni@gmail.com")
  const [password, setPassword] = useState(""); //useState("younes")
  const [showPassword, setShowPassword] = useState(false)
  const { state, dispatch } = useContext(AppContext)
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  const handleSubmit = async (e) => {
    e.preventDefault()
    dispatch({ type: "LOGIN_START" })
    try {
      const response = await axios.post("/api/partner/login", {
        email,
        password,
      })
      if (!response.data.success) {
        alert("Invalid email or password")
        return
      }

      const session = await axios.get("/api/partner/session")
      const user = session.data.user

      dispatch({ type: "LOGIN_SUCCESS", payload: { user: { email }, isAdmin : session.data.payload.isAdmin, session: session.data } })
      localStorage.setItem("user", JSON.stringify({ user: { email }, session: session.data }))
      const isAdmin = session.data.payload.isAdmin;
      console.log("isAdmin", isAdmin)
      let path = "/home";
      if (!isAdmin){
        path = "/partner-info/"+session.data.payload.store_id;//store_id
      }
      const redirectTo = !isAdmin? path : location.state?.from?.pathname || path
      console.log("redirectTo", redirectTo)
      navigate(redirectTo, { replace: true })
      if (!isAdmin) {
        setTimeout(() => {
          navigate(redirectTo, { replace: true })
          
        }, 500);
      }
    } catch (error) {
      dispatch({ type: "LOGIN_FAILURE", payload: error.response?.data?.error || "Login failed" })
    }
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <LoginContainer maxWidth={false}>
      {/* Decorative circles */}
      <Circle size="300px" top="-50px" left="-50px" opacity={0.4} />
      <Circle size="200px" bottom="10%" right="5%" opacity={0.3} />
      <Circle size="150px" top="60%" left="10%" opacity={0.2} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          position: "relative",
          zIndex: 5,
          py: 4,
        }}
      >
        <Fade in={true} timeout={800}>
          <GlassCard elevation={0}>
            <LogoContainer>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                  mb: 2,
                }}
              >
                <CreditCardIcon sx={{ fontSize: 36, color: "white" }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" align="center" sx={{ mb: 0.5 }}>
                CARDZ HUB
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                Sign in to your account
              </Typography>
            </LogoContainer>

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <StyledTextField
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="toggle password visibility" onClick={toggleShowPassword} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ height: 20, mb: 1 }}>
                  {state.error && (
                    <Alert
                      severity="error"
                      sx={{
                        borderRadius: 1.5,
                        py: 0,
                        "& .MuiAlert-message": { py: 0.5 },
                      }}
                    >
                      {state.error}
                    </Alert>
                  )}
                </Box>

                <LoginButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={state.loading}
                  startIcon={state.loading ? null : <LoginIcon />}
                >
                  {state.loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
                </LoginButton>
              </Box>
            </form>

            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Secure Login
              </Typography>
            </Divider>

            <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
              By signing in, you agree to our Terms of Service and Privacy Policy.
            </Typography>
          </GlassCard>
        </Fade>
      </Box>
    </LoginContainer>
  )
}

export default Login

